/**
 * Analytics — the single reading of what the backend returns.
 *
 * Network demand is deliberately not a number. The backend returns a rank, a three-step
 * band and a trend for each model, never units, revenue, store counts or another store's
 * price: most models are sold by a single store, so any such figure would be that store's.
 * Do not add a "units sold in the network" column here — there is nothing to show it from.
 */

export type RangeDays = 7 | 30 | 90;
export const RANGES: RangeDays[] = [7, 30, 90];

export interface SalesPoint {
  date: string;
  revenue: number;
  orders: number;
  units: number;
}

export interface SalesOverview {
  currency: string | null;
  timezone: string;
  days: number;
  from: string;
  to: string;
  series: SalesPoint[];
  previous_series: SalesPoint[];
  totals: {
    revenue: number;
    orders: number;
    units: number;
    supplier_units: number;
    own_units: number;
    other_currency_orders: number;
  };
  previous_totals: { revenue: number; orders: number; units: number };
}

export type Demand = 'very_high' | 'high' | 'steady';
export type Trend = 'new' | 'up' | 'steady' | 'down';

export type ModelStatus =
  | 'selling'
  | 'removed'
  | 'listed'
  | 'listed_no_stock'
  | 'available'
  | 'other_supplier'
  | 'not_available';

export interface NetworkModel {
  rank: number;
  sku: string;
  title: string | null;
  brand: string | null;
  photo_url: string | null;
  demand: Demand;
  trend: Trend;
  top_sizes: string[];
  status: ModelStatus;
  my_units: number;
  from_price: number | null;
  addable_from: string[];
  addable_in_stock: boolean;
  other_suppliers: number;
}

export type PriceVerdict = 'above' | 'in_line' | 'below' | 'sold_out' | 'not_listed' | 'unknown';

export interface PriceCheckItem {
  sku: string;
  rank: number;
  verdict: PriceVerdict;
  gap_band: '5-15' | '15-30' | '30+' | null;
  my_price: number | null;
  my_price_max: number | null;
  currency: string | null;
}

export type SupplierStatus = 'in_stock' | 'out_of_stock' | 'not_carried';

export interface SupplierModel {
  rank: number;
  sku: string;
  title: string | null;
  brand: string | null;
  photo_url: string | null;
  demand: Demand;
  trend: Trend;
  top_sizes: string[];
  status: SupplierStatus;
}

export interface SupplierResult {
  sku: string;
  title: string | null;
  won: number;
  lost_on_price: number;
  lost_no_stock: number;
  missed: number;
  avg_gap: number | null;
}

export interface ProviderDemand {
  days: number;
  summary: { won: number; lost_on_price: number; lost_no_stock: number; avg_gap: number | null };
  results: SupplierResult[];
  models: SupplierModel[];
}

export const DEMAND_LABEL: Record<Demand, string> = {
  very_high: 'Very high',
  high: 'High',
  steady: 'Steady',
};

export const TREND_LABEL: Record<Trend, string> = {
  new: 'New this period',
  up: 'Rising',
  steady: 'Holding',
  down: 'Cooling',
};

export const STATUS_LABEL: Record<ModelStatus, string> = {
  selling: 'You sell this',
  removed: 'You removed this',
  listed: 'In your store',
  listed_no_stock: 'In your store, suppliers out of stock',
  available: 'Not in your store',
  other_supplier: 'Not in your store',
  not_available: 'No supplier has it yet',
};

export const GAP_LABEL: Record<NonNullable<PriceCheckItem['gap_band']>, string> = {
  '5-15': '5–15% above',
  '15-30': '15–30% above',
  '30+': 'more than 30% above',
};

/** Change against the previous period, or null when there is nothing to compare with. */
export function pctChange(current: number, previous: number): number | null {
  if (!previous) return null;
  return ((current - previous) / previous) * 100;
}

export function formatDelta(delta: number | null): string {
  if (delta === null) return '—';
  const rounded = Math.round(delta);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

export function formatMoney(value: number, currency: string | null, digits = 0): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'EUR',
      maximumFractionDigits: digits,
      minimumFractionDigits: digits,
    }).format(value);
  } catch {
    return `${value.toFixed(digits)} ${currency ?? ''}`.trim();
  }
}

export function formatDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export interface MissedDemand {
  /** Hot models this store could sell but does not: one click (or a restore) away. */
  addable: NetworkModel[];
  /** Hot models that need a supplier this store is not connected to. */
  needSupplier: NetworkModel[];
  /** Hot models nobody supplies yet. Shown so the gap is visible, with nothing to click. */
  unsupplied: NetworkModel[];
  /** In the store, but priced above where the model is selling. */
  lostOnPrice: Array<{ model: NetworkModel; price: PriceCheckItem }>;
  /** In the store, selling elsewhere, and out of stock here. */
  soldOut: NetworkModel[];
}

/**
 * Splits the ranked network list into the store's gaps. Order within each group stays the
 * network rank, so the hottest gap is always first.
 */
export function missedDemand(models: NetworkModel[], prices: PriceCheckItem[]): MissedDemand {
  const priceBySku = new Map(prices.map((p) => [p.sku, p]));
  const out: MissedDemand = { addable: [], needSupplier: [], unsupplied: [], lostOnPrice: [], soldOut: [] };

  for (const model of models) {
    const price = priceBySku.get(model.sku);
    if (model.status === 'available' || model.status === 'removed') out.addable.push(model);
    else if (model.status === 'other_supplier') out.needSupplier.push(model);
    else if (model.status === 'not_available') out.unsupplied.push(model);
    else if (price?.verdict === 'above') out.lostOnPrice.push({ model, price });
    else if (model.status === 'listed_no_stock' || price?.verdict === 'sold_out') out.soldOut.push(model);
  }
  return out;
}

/** A round axis maximum at or above `value`, with at most 4 even steps below it. */
export function niceMax(value: number): number {
  if (!(value > 0)) return 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  for (const step of [1, 2, 4, 5, 8, 10]) {
    if (step * magnitude >= value) return step * magnitude;
  }
  return 10 * magnitude;
}
